<template>
  <div id="historicalPrices">
    <div class="historicalPrices bg-gold-50 py-2 lg:py-10 border border-gold-500">

      <!-- Loading message -->
      <div v-if="loading" class="loadingPrices THISTEST">
        {{ translateCustom("Fetching historical prices. Please wait") }}
        Henter historiske priser. Vennligst vent
      </div>
      <!-- // Loading message -->

      <!-- Error message -->
      <div v-else-if="error" class="errorPrices bg-red-700 mb-6 -mt-2 lg:-mt-10 p-8 text-center text-white">
        {{ error }}<br />
        <button @click="loadData()" class="bg-gray-200 hover:bg-white focus:bg-white text-gray-900 py-2 px-4 mt-2">
          Prøv på nytt
          {{ translateCustom("Try again") }}
        </button>
      </div>
      <!-- // Error message -->

        <!-- Filter -->
        <div class="lg:flex lg:justify-between px-2 lg:px-10 mb-6 lg:mb-0">
          <!-- Metal -->
          <div class="mb-4 lg:mb-0">
            <h3 class="font-bold mb-0">{{translateCustom("Metal")}}</h3>
            <button
              v-for="el in allData"
              :key="el.metal"
              @click="selectMetal(el.metal)"
              class="hover:bg-gold-700 text-black hover:text-white text-sm py-1 px-4 mr-2"
              :class="computedClassMetal(el.metal)"
            >
              {{ el.metal }}          
            </button>
          </div>
          <!-- // Metal -->

          <!-- Datepickers -->
          <div class="flex mb-4 lg:mb-0">
            <!-- From -->
            <div>
              <h3 class="font-bold mb-0">{{ translateCustom("From") }}</h3>
              <datepicker 
                v-model="timeSpan.from"
                :format="formatDate"
                :monday-first=true
                input-class="graph"
               >
               </datepicker>
            </div>
            <!-- // From -->

            <!-- To -->
            <div>
              <h3 class="font-bold mb-0">{{translateCustom("To")}}</h3>
              <datepicker
                v-model="timeSpan.to"
                :format="formatDate"
                :monday-first=true
                :disabled-dates="{ to: new Date(timeSpan.from) }"
                input-class="graph"
              >
              </datepicker>
            </div>
            <!-- // To -->
          </div>
          <!-- // Datepickers -->
        </div>
        <!-- // Filter -->
        <div class="chart-container p-2 lg:p-10 mb-4 lg:mb-0">
          <line-chart 
            :chartData="chartData"
            :options="chartOptions"
          />
        </div>

        <!-- Currency -->
        <div class="flex justify-center mb-4 mb:mb-0">
          <button
            v-for="(el, key) in currencies"
            :key="key"
            @click="selectCurrency(el)"
            class="hover:bg-gold-700 text-black hover:text-white text-sm py-1 px-4 mr-2"
            :class="computedClassCurrency(el)"
          >
            {{ el }}          
          </button>
        </div>
        <!-- // Currency -->



    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Datepicker from 'vuejs-datepicker'
import LineChart from "./components/Chart.js"

export default {
  name: 'App',
  components: {
    LineChart,
    Datepicker
  },
  props: {
    storecode: {
      type: String,
      default: () => ({})
    }
  },
  data() {
    return {
      chartData: {
        labels: [],
        datasets: [{
          data: []
        }]
      },
      chartOptions: {
					responsive: true,
					hoverMode: 'index',
          stacked: false,
          maintainAspectRatio: false,
					title: {
						display: false,
						text: this.selectedMetal
          },
          legend: {
            display: false
          },
          scales: {
            xAxes: [{
              display: true,
            }],
            yAxes: [{
              display: true,
              beginAtZero: false
            }]
          },
        layout: {
            padding: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0
            }
        }
      },
      allData: [],
      colors: {
        nok: '#983226', // Rust 600
        sek: '#417982', // Turquoise 500
        dkk: '#525557', // Gray 500 
        eur: '#886B3E' // Gold 500
      },
      loading: true,
      select: [],
      timeSpan: {
        from: '2020-10-30',
        to: '2020-11-30'
      },
      error: null,
      selectedMetal: 'AU',
      currencies: ['NOK', 'SEK', 'USD', 'EUR'],
      selectedCurrency: 'nok',
      translations: {
        "Fetching historical prices. Please wait":{
          "no": "Henter historiske priser. Vennligst vent"
        },
        "Try again": {
          "no": "Prøv på nytt"
        },
        "Metal": {
          "no": "Metall",
          "fi": "Metallit"
        },
        "From": {
          "no": "Fra",
          "fi": "Alkaen"
        },
        "To": {
          "no": "Til",
          "fi": "Asti"
        }
      }
    }
  },
  beforeMount() {
    this.setDefaultDate()
  },
  methods: {
    translateCustom(text) {
      console.log('Translating:', text, 'Storecode:', this.storecode);
      return this.translations[text] && this.translations[text][this.storecode] || text;
    },
    async loadData(timeFrom, timeTo) {
      this.loading = true

      // If timeFrom og timeTo is missing
      if (timeFrom == null){
          timeFrom = this.formatDate(this.timeSpan.from)
      }
      if (timeTo == null){
          timeTo = this.formatDate(this.timeSpan.to)
      }

      // NOTE: Hardcoded URL 
      // const url = `https://karasmussen-wordpress.vpstage.no/wp-json/price-history/v1/prices?from=${timeFrom}&to=${timeTo}`
      const url = '/wp-json/price-history/v1/prices?from='+ timeFrom +'&to=' + timeTo
      try {
        const response = await axios.get(url)
        if (response.data.length > 0 && typeof response.data.message === 'undefined') {
          const rawData = response.data
          this.allData = []
          rawData.forEach(metall => {
            this.allData.push(
              {
                metal: metall.Code,
                prices: this.sortData(metall)
              }
            )
          })
          this.selectMetal(this.selectedMetal)
          this.error = null
        } else {
          console.error(response)
          this.error = 'En feil oppsto i uthenting av priser. Vennligst prøv på nytt'
        }
        this.loading = false
      } catch (error) {
         console.error(error);
        this.loading = false
        this.error = 'En feil oppsto i uthenting av priser. Vennligst prøv på nytt'
      }
    },
    sortData(metall) {
      const prices = {}

      metall.Prices.sort((a, b) =>  new Date(a.TradeTime) - new Date(b.TradeTime))

      metall.Prices.forEach(price => {
        if (!Object.prototype.hasOwnProperty.call(prices, price.Currency.toLowerCase())) {
          prices[price.Currency.toLowerCase()] = []
        }
        prices[price.Currency.toLowerCase()].push({
          price: price.BidGram.toFixed(2),
          tradeTime: new Date(price.TradeTime)
        })
      })

      return prices
      
    },
    selectMetal(metal) {

      if (typeof metal === 'undefined') {
        metal = this.selectedMetal
      }

      // Get "stored" data (this must be replaces by an AJAX-call)
      const data = this.allData.filter(price => price.metal === metal);
      const metalPrices = data[0].prices[this.selectedCurrency];

      if (metalPrices.length > 0) {
        // Push labels for X-axis for this metal
        let labels = metalPrices.map(price => {
          const time = new Date(price.tradeTime)
          return `${time.getDate()}.${time.getMonth() + 1}.${time.getFullYear().toString().substr(-2)}`
        })

        // Push prices for this metal and currency
        const prices = metalPrices.map(entry => entry.price)

        // Create multiple datasets for all the currencies for the selected metal
        let datasetsCurrency = [];
        datasetsCurrency.push({
            label: this.selectedCurrency,
            backgroundColor: this.colors[this.selectedCurrency],
            borderColor: this.colors[this.selectedCurrency],
            borderJoinStyle: "miter",
            lineTension: 0,
            fill: false,
            data: prices
        })

        this.chartData = {
          labels: labels,
          datasets: datasetsCurrency
        };      

        // Update title for chart
        this.chartOptions.title.text = metal
        this.selectedMetal = metal
      } else {
        console.error('En feil i datagrunnlaget for dette metallet: Finner ingen priser for: ' + metal)
      }
    },
    formatDate(el) {
      const date = new Date(el)
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate()}`
    },
    setDefaultDate() {
      const date = new Date()
      const monthago =  new Date()
      monthago.setMonth(monthago.getMonth() - 2)
      this.timeSpan.to = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate()}`
      this.timeSpan.from = `${monthago.getFullYear()}-${monthago.getMonth() + 1}-${monthago.getDate()}`
    },
    computedClassMetal(metal) {
      if (metal === this.selectedMetal) {
        return 'bg-turquoise'
      } else {
        return 'bg-gold-500'
      }
    },
    computedClassCurrency(currency) {
      if (currency.toLowerCase() === this.selectedCurrency) {
        return 'bg-turquoise'
      } else {
        return 'bg-gold-500'
      }
    },
    selectCurrency(currency) {
      this.selectedCurrency = currency.toLowerCase()
      this.selectMetal(this.selectedMetal)
    }
  },
  mounted() {
    // this.setDefaultDate()
    this.loadData()
  },
  watch: {
    allData: {
      handler() {
        this.selectMetal(this.selectedMetal)
      },
      deep: true
    },
    timeSpan: {
      handler() {
        this.loadData(this.formatDate(this.timeSpan.from), this.formatDate(this.timeSpan.to) )
      },
      deep: true
    }
  }
}
</script>

<style scoped>
#app {
  position: relative;
}

.historicalPrices {
  position: relative;
  width: 100%;
}

.chart-container {
  max-height: 500px;
}

.chart-container > div {
  position: relative;
  width: 100%;
}

.loadingPrices {
  position: absolute;
  top: 0;
  left: 0;
  transform: translate3d(0,0,0);
  background-color: rgba(0,0,0,0.7);
  width: 100%;
  height: 100%;
  text-align: center;
  padding: 100px 0;
  color: #fff;
  z-index: 100;
}
</style>
